import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle, faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import './UnitChangeConfirmationModal.css';
import { formatQuantity, formatUnitLabel } from '../utils/unitDisplay';

/**
 * Unit Change Confirmation Modal
 * 
 * Displays comprehensive impact analysis before applying a unit change to an inventory item.
 * Shows affected recipes, batches, preparations, and inventory with before/after quantities.
 * 
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - previewData: object - Preview data from /api/inventory/items/change-unit?preview=true
 * - onConfirm: function - Callback when user confirms (receives conversionFactor)
 * - onCancel: function - Callback when user cancels
 * - isLoading: boolean - Whether confirmation is in progress
 */
const UnitChangeConfirmationModal = ({ 
  isOpen, 
  previewData, 
  onConfirm, 
  onCancel,
  isLoading = false 
}) => {
  const [customConversionFactor, setCustomConversionFactor] = useState('');
  const [showAllRecipes, setShowAllRecipes] = useState(false);
  const [showAllBatches, setShowAllBatches] = useState(false);

  if (!isOpen || !previewData) return null;

  const {
    item,
    conversionFactor,
    inventory,
    affectedRecipes = [],
    affectedBatches = [],
    affectedPreparations = [],
    affectedComplimentary = [],
    summary,
    warnings = []
  } = previewData;

  const isCrossFamily = item?.isCrossFamily || false;
  const requiresCustomFactor = isCrossFamily && !conversionFactor;

  const handleConfirm = () => {
    const factor = requiresCustomFactor ? parseFloat(customConversionFactor) : conversionFactor;
    
    if (!factor || factor <= 0) {
      alert('Please provide a valid conversion factor greater than 0');
      return;
    }

    onConfirm(factor);
  };

  const displayRecipes = showAllRecipes ? affectedRecipes : affectedRecipes.slice(0, 5);
  const displayBatches = showAllBatches ? affectedBatches : affectedBatches.slice(0, 5);

  return (
    <div className="unit-change-modal-overlay" onClick={onCancel}>
      <div className="unit-change-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`unit-change-modal-header ${isCrossFamily ? 'cross-family' : 'same-family'}`}>
          <div>
            <h2>
              <FontAwesomeIcon 
                icon={isCrossFamily ? faExclamationTriangle : faInfoCircle} 
                style={{ marginRight: 10 }} 
              />
              {isCrossFamily ? 'Cross-Family Unit Change' : 'Unit Change Confirmation'}
            </h2>
            <p className="unit-change-subtitle">
              {item?.name} • {formatUnitLabel(item?.currentUnit)} → {formatUnitLabel(item?.newUnit)}
            </p>
          </div>
          <button className="unit-change-close-btn" onClick={onCancel} disabled={isLoading}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className={`unit-change-warnings ${isCrossFamily ? 'critical' : 'info'}`}>
            {warnings.map((warning, idx) => (
              <div key={idx} className="unit-change-warning-item">
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8 }} />
                {warning}
              </div>
            ))}
          </div>
        )}

        {/* Conversion Factor Input (Cross-Family Only) */}
        {requiresCustomFactor && (
          <div className="unit-change-conversion-input">
            <label>
              <strong>Conversion Factor (Required)</strong>
              <span className="help-text">
                1 {item?.currentUnit} of {item?.name} = ? {item?.newUnit}
              </span>
            </label>
            <input
              type="number"
              step="0.001"
              min="0.001"
              value={customConversionFactor}
              onChange={(e) => setCustomConversionFactor(e.target.value)}
              placeholder="e.g., 1.0 if 1g = 1ml"
              className="unit-change-conversion-input-field"
              disabled={isLoading}
            />
          </div>
        )}

        {/* Conversion Summary */}
        {conversionFactor && !requiresCustomFactor && (
          <div className="unit-change-conversion-summary">
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981', marginRight: 8 }} />
            <span>
              <strong>Automatic Conversion:</strong> 1 {formatUnitLabel(item?.currentUnit)} = {conversionFactor} {formatUnitLabel(item?.newUnit)}
            </span>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="unit-change-modal-body">
          
          {/* Inventory Impact */}
          <section className="unit-change-section">
            <h3>📦 Inventory Impact</h3>
            <div className="unit-change-inventory-card">
              <div className="unit-change-stat">
                <span className="label">Current Stock:</span>
                <span className="value">{formatQuantity(inventory?.oldStock, item?.currentUnit)}</span>
              </div>
              <div className="unit-change-arrow">→</div>
              <div className="unit-change-stat">
                <span className="label">New Stock:</span>
                <span className="value highlighted">{formatQuantity(inventory?.newStock, item?.newUnit)}</span>
              </div>
            </div>
          </section>

          {/* Affected Recipes */}
          {affectedRecipes.length > 0 && (
            <section className="unit-change-section">
              <h3>🍳 Affected Recipes ({affectedRecipes.length})</h3>
              <div className="unit-change-table-wrapper">
                <table className="unit-change-table">
                  <thead>
                    <tr>
                      <th>Recipe Name</th>
                      <th>Current Quantity</th>
                      <th></th>
                      <th>New Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRecipes.map((recipe, idx) => (
                      <tr key={idx}>
                        <td>{recipe.recipeName}</td>
                        <td>{formatQuantity(recipe.oldQty, item?.currentUnit)}</td>
                        <td>→</td>
                        <td className="highlighted">{formatQuantity(recipe.newQty, item?.newUnit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {affectedRecipes.length > 5 && !showAllRecipes && (
                <button 
                  className="unit-change-show-more-btn"
                  onClick={() => setShowAllRecipes(true)}
                >
                  Show all {affectedRecipes.length} recipes
                </button>
              )}
            </section>
          )}

          {/* Affected Batches */}
          {affectedBatches.length > 0 && (
            <section className="unit-change-section">
              <h3>📦 Affected Inventory Batches ({affectedBatches.length})</h3>
              <div className="unit-change-table-wrapper">
                <table className="unit-change-table">
                  <thead>
                    <tr>
                      <th>Batch ID</th>
                      <th>Ref</th>
                      <th>Current Qty</th>
                      <th></th>
                      <th>New Qty</th>
                      <th>Unit Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayBatches.map((batch, idx) => (
                      <tr key={idx}>
                        <td>{batch.batchId}</td>
                        <td>{batch.invoiceReference || batch.supplierName || 'N/A'}</td>
                        <td>{formatQuantity(batch.oldRemainingQty, item?.currentUnit)}</td>
                        <td>→</td>
                        <td className="highlighted">{formatQuantity(batch.newRemainingQty, item?.newUnit)}</td>
                        <td>
                          ₹{batch.oldUnitCost?.toFixed(2)}/{formatUnitLabel(item?.currentUnit)} → ₹{batch.newUnitCost?.toFixed(2)}/{formatUnitLabel(item?.newUnit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {affectedBatches.length > 5 && !showAllBatches && (
                <button 
                  className="unit-change-show-more-btn"
                  onClick={() => setShowAllBatches(true)}
                >
                  Show all {affectedBatches.length} batches
                </button>
              )}
            </section>
          )}

          {/* Affected Preparations */}
          {affectedPreparations.length > 0 && (
            <section className="unit-change-section">
              <h3>🥘 Affected Preparations ({affectedPreparations.length})</h3>
              <div className="unit-change-table-wrapper">
                <table className="unit-change-table">
                  <thead>
                    <tr>
                      <th>Preparation Name</th>
                      <th>Current Quantity</th>
                      <th></th>
                      <th>New Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affectedPreparations.map((prep, idx) => (
                      <tr key={idx}>
                        <td>{prep.prepName}</td>
                        <td>{formatQuantity(prep.oldQty, item?.currentUnit)}</td>
                        <td>→</td>
                        <td className="highlighted">{formatQuantity(prep.newQty, item?.newUnit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Affected Complimentary Recipes */}
          {affectedComplimentary.length > 0 && (
            <section className="unit-change-section">
              <h3>🎁 Affected Complimentary ({affectedComplimentary.length})</h3>
              <div className="unit-change-table-wrapper">
                <table className="unit-change-table">
                  <thead>
                    <tr>
                      <th>Complimentary Recipe</th>
                      <th>Current Quantity</th>
                      <th></th>
                      <th>New Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affectedComplimentary.map((comp, idx) => (
                      <tr key={idx}>
                        <td>{comp.recipeName}</td>
                        <td>{formatQuantity(comp.oldQty, item?.currentUnit)}</td>
                        <td>→</td>
                        <td className="highlighted">{formatQuantity(comp.newQty, item?.newUnit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Historical Data Notice */}
          <div className="unit-change-notice">
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 8 }} />
            <strong>Note:</strong> Historical data in reports (TSR, Stock Reports) will remain unchanged.
            Only future entries will use the new unit.
          </div>
        </div>

        {/* Footer */}
        <div className="unit-change-modal-footer">
          <button 
            className="unit-change-cancel-btn" 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            className="unit-change-confirm-btn" 
            onClick={handleConfirm}
            disabled={isLoading || (requiresCustomFactor && !customConversionFactor)}
          >
            {isLoading ? 'Applying...' : 'Confirm & Apply'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnitChangeConfirmationModal;
